import { useCallback } from "react";
import { WorkLog } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryCache } from "@ws-react-query";
import { queriesWorkLogs } from "@query";
import {
  bulkActionFactory,
  useForegroundBulkAction,
  WSAlertConfig
} from "../../../modules/BulkActions";

function invalidateWorkLogQueries() {
  WSQueryCache.invalidateQueries(queriesWorkLogs.listWorkLogs.queryKeyBase);
  WSQueryCache.invalidateQueries(queriesWorkLogs.getWorkLog.queryKeyBase);
}

function reminderAlert(
  workLogsCount: number,
  actionVerb: string
): WSAlertConfig {
  const plural = workLogsCount !== 1 ? "s" : "";
  return {
    theme: "warning",
    children: `Reminder: Your comment will be applied to all ${workLogsCount} work log${plural}. If you need to provide different feedback for each work log, ${actionVerb} each one individually instead.`
  };
}

const PUBLIC_PILL = {
  icon: "view" as const,
  text: "Public",
  theme: "neutral" as const
};

export function useWorkLogBulkActions() {
  const runBulkAction = useForegroundBulkAction();
  const [approveWorkLog] = queriesWorkLogs.approveWorkLog.useMutation({
    defaultDependenciesLevel: "none"
  });
  const [rejectWorkLog] = queriesWorkLogs.rejectWorkLog.useMutation({
    defaultDependenciesLevel: "none"
  });
  const [requestWorkLogEdit] = queriesWorkLogs.requestWorkLogEdit.useMutation({
    defaultDependenciesLevel: "none"
  });

  const approve = useCallback(
    (workLogs: WorkLog[], onSuccess?: () => void) => {
      const workLogsCount = workLogs.length;

      return runBulkAction({
        actionName: `Approve ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}`,
        itemCount: workLogsCount,
        confirmDescription: `Are you sure you want to approve ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}?`,
        confirmLabel: "Yes, approve work logs",
        confirmComment: {
          description: `Comment will be applied to all ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}. If you need to provide different feedback for each work log, approve each one individually instead.`,
          placeholder: "Provide specific details about the decision",
          pill: PUBLIC_PILL,
          alert: reminderAlert(workLogsCount, "approve")
        },
        confirmAlert: {
          theme: "info",
          title: "About approved work logs",
          icon: "info-circle",
          children:
            "These work logs will be converted to invoices and added to your next payment cycle. Note: You can still correct unpaid invoices and work logs if needed. Paid invoices and related work logs cannot be changed."
        },
        prepare: async () => workLogs,
        process: async (data, onProgress) => {
          const bulk = bulkActionFactory<WorkLog>({
            onProgress,
            // TODO(v3): v3 `approveWorkLog` takes no comment — the collected
            // comment is dropped (no field on the approve transition).
            action: async wl => approveWorkLog({ workLogId: wl.id! }),
            onFinish: () => {
              invalidateWorkLogQueries();
              onSuccess?.();
            }
          });
          const { successItems, failedItems } = await bulk.run(data);
          return {
            total: data.length,
            processed: successItems.length + failedItems.length,
            successful: successItems.length,
            failed: failedItems.length
          };
        }
      });
    },
    [runBulkAction, approveWorkLog]
  );

  const reject = useCallback(
    (workLogs: WorkLog[], onSuccess?: () => void) => {
      const workLogsCount = workLogs.length;

      return runBulkAction({
        actionName: `Reject ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}`,
        itemCount: workLogsCount,
        confirmDescription: `Are you sure you want to reject ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}?`,
        confirmLabel: "Yes, reject work logs",
        confirmDestructive: true,
        confirmComment: {
          description: `Comment will be applied to all ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}. If you need to provide different feedback for each work log, reject each one individually instead.`,
          placeholder: "Provide specific details about the decision",
          pill: PUBLIC_PILL,
          alert: reminderAlert(workLogsCount, "reject")
        },
        confirmAlert: {
          theme: "info",
          title: "About rejected work logs",
          icon: "info-circle",
          children:
            "Once a work log is rejected, it cannot be reverted. It will not be invoiced. Please provide the necessary detail to your contractors about the rejection."
        },
        prepare: async () => workLogs,
        process: async (data, onProgress, comment) => {
          const bulk = bulkActionFactory<WorkLog>({
            onProgress,
            action: async wl =>
              rejectWorkLog({
                workLogId: wl.id!,
                rejectWorkLogRequest: { reason: comment! }
              }),
            onFinish: () => {
              invalidateWorkLogQueries();
              onSuccess?.();
            }
          });
          const { successItems, failedItems } = await bulk.run(data);
          return {
            total: data.length,
            processed: successItems.length + failedItems.length,
            successful: successItems.length,
            failed: failedItems.length
          };
        }
      });
    },
    [runBulkAction, rejectWorkLog]
  );

  const returnForEditing = useCallback(
    (workLogs: WorkLog[], onSuccess?: () => void) => {
      const workLogsCount = workLogs.length;

      return runBulkAction({
        actionName: `Return ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""} for editing`,
        itemCount: workLogsCount,
        confirmDescription: `Are you sure you want to return ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""} for editing?`,
        confirmLabel: "Yes, return for editing",
        confirmComment: {
          label: "Comment *",
          description: `Comment will be applied to all ${workLogsCount} work log${workLogsCount !== 1 ? "s" : ""}. If you need to provide different feedback for each work log, return each one individually instead.`,
          placeholder: "Provide specific details about the requested changes",
          required: true,
          pill: PUBLIC_PILL,
          alert: reminderAlert(workLogsCount, "return")
        },
        confirmAlert: {
          theme: "info",
          title: "About requesting changes on work logs",
          icon: "info-circle",
          children:
            "These work logs will be sent back to contractors for corrections. Each contractor will make changes and resubmit for your approval. Provide specific guidance below about what needs to change."
        },
        prepare: async () => workLogs,
        process: async (data, onProgress, comment) => {
          const bulk = bulkActionFactory<WorkLog>({
            onProgress,
            // TODO(v3): request-changes maps to v3 `requestWorkLogEdit`; the
            // comment is sent as `voidInvoiceRequest.reason` (semantic shift).
            action: async wl =>
              requestWorkLogEdit({
                workLogId: wl.id!,
                voidInvoiceRequest: { reason: comment! }
              }),
            onFinish: () => {
              invalidateWorkLogQueries();
              onSuccess?.();
            }
          });
          const { successItems, failedItems } = await bulk.run(data);
          return {
            total: data.length,
            processed: successItems.length + failedItems.length,
            successful: successItems.length,
            failed: failedItems.length
          };
        }
      });
    },
    [runBulkAction, requestWorkLogEdit]
  );

  return { approve, reject, returnForEditing };
}
